// Gráficos em SVG simples, sem biblioteca.

export interface Series {
  label: string;
  color: string;
  values: (number | null)[];
}

export function LineChart({
  labels,
  series,
  max = 10,
  height = 220,
}: {
  labels: string[];
  series: Series[];
  max?: number;
  height?: number;
}) {
  const width = 640;
  const pad = { l: 32, r: 12, t: 12, b: 28 };
  const w = width - pad.l - pad.r;
  const h = height - pad.t - pad.b;
  const x = (i: number) => pad.l + (labels.length <= 1 ? w / 2 : (i / (labels.length - 1)) * w);
  const y = (v: number) => pad.t + h - (v / max) * h;
  const ticks = [0, max / 2, max];
  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        role="img"
        aria-label="Evolução das notas"
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={width - pad.r} y1={y(t)} y2={y(t)} stroke="#E4E7EC" />
            <text x={pad.l - 6} y={y(t) + 4} fontSize="11" textAnchor="end" fill="#667085">
              {t}
            </text>
          </g>
        ))}
        {labels.map((l, i) => (
          <text
            // biome-ignore lint/suspicious/noArrayIndexKey: a posição no eixo é a identidade do ponto.
            key={`${l}-${i}`}
            x={x(i)}
            y={height - 8}
            fontSize="11"
            textAnchor="middle"
            fill="#667085"
          >
            {l}
          </text>
        ))}
        {series.map((s) => {
          const pts = s.values
            .map((v, i) => (v === null ? null : `${x(i)},${y(v)}`))
            .filter(Boolean);
          return (
            <g key={s.label}>
              <polyline
                points={pts.join(" ")}
                fill="none"
                stroke={s.color}
                strokeWidth={s.label === "Média" ? 3 : 2}
              />
              {s.values.map((v, i) =>
                v === null ? null : (
                  <circle
                    // biome-ignore lint/suspicious/noArrayIndexKey: a posição no eixo é a identidade do ponto.
                    key={`${s.label}-${labels[i]}-${i}`}
                    cx={x(i)}
                    cy={y(v)}
                    r={3}
                    fill={s.color}
                  />
                ),
              )}
            </g>
          );
        })}
      </svg>
      <div className="row small" style={{ marginTop: 6 }}>
        {series.map((s) => (
          <span key={s.label} className="row" style={{ gap: 6 }}>
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background: s.color,
                display: "inline-block",
              }}
            />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function BarChart({
  data,
  height = 180,
  color = "#1F4FD1",
}: {
  data: { label: string; value: number }[];
  height?: number;
  color?: string;
}) {
  const width = 640;
  const pad = { l: 28, r: 8, t: 10, b: 26 };
  const max = Math.max(1, ...data.map((d) => d.value));
  const w = width - pad.l - pad.r;
  const h = height - pad.t - pad.b;
  const bw = data.length ? w / data.length : w;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Leads por dia">
      <line x1={pad.l} x2={width - pad.r} y1={pad.t + h} y2={pad.t + h} stroke="#E4E7EC" />
      <text x={pad.l - 6} y={pad.t + 8} fontSize="11" textAnchor="end" fill="#667085">
        {max}
      </text>
      {data.map((d, i) => {
        const bh = (d.value / max) * h;
        return (
          <g key={d.label}>
            <rect
              x={pad.l + i * bw + 2}
              y={pad.t + h - bh}
              width={Math.max(2, bw - 4)}
              height={bh}
              rx={3}
              fill={color}
            >
              <title>{`${d.label}: ${d.value}`}</title>
            </rect>
            {data.length <= 16 || i % Math.ceil(data.length / 10) === 0 ? (
              <text
                x={pad.l + i * bw + bw / 2}
                y={height - 8}
                fontSize="10"
                textAnchor="middle"
                fill="#667085"
              >
                {d.label}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

export function ScoreBar({
  label,
  value,
  max = 10,
}: {
  label: string;
  value: number;
  max?: number;
}) {
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="row between small">
        <span>{label}</span>
        <strong>{value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}</strong>
      </div>
      <div className="score-bar">
        <span style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
      </div>
    </div>
  );
}
